import React, { useState } from 'react';
import ReactHintFactory from 'react-hint';
import katex from 'katex';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';
import { PreEditor } from 'components/editor/inputs';

import type { BlockMath } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const ReactHint = ReactHintFactory(React);

const EditorBlockMath = ({
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockMath> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, theme } = useReduxState();

  const [error, setError] = useState<boolean>(false);

  const renderMathHtml = (text: string) => {
    try {
      const html = katex.renderToString(text, {
        displayMode: true
      });
      if (error) {
        setError(false);
      }
      return html;
    } catch (err) {
      if (!error) {
        setError(true);
      }
      return undefined;
    }
  };

  const handleChange = (text: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            text
          }
        }
      }
    });
  };

  const handleInfoRender = () => {
    return (
      <div className="vuo-tooltip__content">
        {`${messages.editorBlockMathInfoText} `}
        <a
          className="link"
          href="https://katex.org/docs/supported.html"
          rel="noopener noreferrer"
          target="_blank"
        >
          {messages.editorBlockMathInfoLink}
        </a>

        <style jsx>{`
          .link {
            color: #ddd;
          }
        `}</style>
      </div>
    );
  };

  return (
    <div>
      <ReactHint
        attribute="data-math-info"
        autoPosition={true}
        className="vuo-tooltip"
        delay={50}
        events={{ click: true, focus: false, hover: false }}
        onRenderContent={handleInfoRender}
        persist={true}
      />

      <div className="content">
        <PreEditor
          isPreview={isPreview}
          onChange={handleChange}
          placeholder={messages.editorBlockMathPlaceholder}
          text={data.locales[languageVersion].translatable.text || ''}
          title={
            <button className="info-button" data-math-info={true}>
              {messages.blocksMath}
              <Icon icon="question-circle" style={{ marginLeft: 4 }} />
            </button>
          }
          translationReferenceLanguage={editorLanguageActive ?? null}
          translationReferenceText={
            editorLanguageActive
              ? data.locales[editorLanguageReference]?.translatable?.text || ''
              : null
          }
        />
      </div>

      <div className="preview">
        {error ? (
          <div className="preview-error">
            {messages.editorBlockMathInvalidSyntax}
          </div>
        ) : data.locales[languageVersion].translatable.text ? (
          <div
            dangerouslySetInnerHTML={{
              __html: renderMathHtml(
                data.locales[languageVersion].translatable.text
              )
            }}
          />
        ) : (
          <div className="preview-empty">
            {messages.editorBlockMathPreviewEmpty}
          </div>
        )}
      </div>

      <style jsx>{`
        .info-button {
          background-color: transparent;
          border: 0;
          color: ${theme.textColorLight};
          cursor: pointer;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 700;
          padding: 0;
        }
        .preview-empty,
        .preview-error {
          color: ${theme.textColorLight};
          font-size: 16px;
          font-style: italic;
          margin: 10px 0 15px;
          text-align: center;
        }
        .preview-error {
          color: ${theme.dangerColor};
        }
      `}</style>
    </div>
  );
};

export default EditorBlockMath;
